/**
 * Bot Detection Service
 * Comprehensive bot detection for user registration
 */

import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase.config';

export interface BotDetectionResult {
  isBot: boolean;
  confidence: number; // 0-100
  reasons: string[];
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  fingerprint: string;
  timestamp: Date;
}

export interface BotDetectionData {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  plugins: string[];
  canvasFingerprint: string;
  webglFingerprint: string;
  audioFingerprint: string;
  fonts: string[];
  behaviorData: BehaviorData;
  ipAddress?: string;
  recaptchaScore?: number;
}

export interface BehaviorData {
  typingSpeed: number; // characters per second
  mouseMovements: number; // total mouse movements
  clickPatterns: number[]; // time between clicks
  scrollBehavior: number; // scroll patterns
  formFillTime: number; // time to fill form
  hesitationTime: number; // time between fields
  errorRate: number; // number of errors made
  copyPasteDetected: boolean;
  tabSwitchCount: number;
  focusTime: number; // time focused on form
}

export interface BotAttempt {
  id: string;
  fingerprint: string;
  userAgent: string;
  ipAddress?: string;
  isBot: boolean;
  confidence: number;
  reasons: string[];
  riskLevel: string;
  timestamp: Date;
  recaptchaScore?: number;
  behaviorData: BehaviorData;
}

class BotDetectionService {
  private static instance: BotDetectionService;
  private behaviorTracker: BehaviorData = {
    typingSpeed: 0,
    mouseMovements: 0,
    clickPatterns: [],
    scrollBehavior: 0,
    formFillTime: 0,
    hesitationTime: 0,
    errorRate: 0,
    copyPasteDetected: false,
    tabSwitchCount: 0,
    focusTime: 0
  };
  private startTime: number = 0;
  private lastActivity: number = 0;
  private mouseMoveCount: number = 0;
  private clickTimes: number[] = [];
  private errors: number = 0;

  private constructor() {
    this.initializeBehaviorTracking();
  }

  public static getInstance(): BotDetectionService {
    if (!BotDetectionService.instance) {
      BotDetectionService.instance = new BotDetectionService();
    }
    return BotDetectionService.instance;
  }

  private initializeBehaviorTracking(): void {
    this.startTime = Date.now();
    this.lastActivity = Date.now();

    // Track mouse movements
    document.addEventListener('mousemove', () => {
      this.mouseMoveCount++;
      this.lastActivity = Date.now();
    });

    // Track clicks
    document.addEventListener('click', () => {
      const now = Date.now();
      if (this.clickTimes.length > 0) {
        const timeSinceLastClick = now - this.clickTimes[this.clickTimes.length - 1];
        this.behaviorTracker.clickPatterns.push(timeSinceLastClick);
      }
      this.clickTimes.push(now);
      this.lastActivity = now;
    });

    // Track scroll behavior
    document.addEventListener('scroll', () => {
      this.behaviorTracker.scrollBehavior++;
      this.lastActivity = Date.now();
    });

    // Track tab switches
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.behaviorTracker.tabSwitchCount++;
      }
    });

    // Track focus time
    document.addEventListener('focus', () => {
      this.startTime = Date.now();
    });

    document.addEventListener('blur', () => {
      if (this.startTime > 0) {
        this.behaviorTracker.focusTime += Date.now() - this.startTime;
      }
    });
  }

  public async detectBot(formData: any, recaptchaToken?: string): Promise<BotDetectionResult> {
    // Quick bypass for development if reCAPTCHA is causing issues
    const isDevelopment = import.meta.env.DEV;
    if (isDevelopment && !recaptchaToken) {
      return {
        isBot: false,
        confidence: 10,
        reasons: ['Development mode - reCAPTCHA bypassed'],
        riskLevel: 'low',
        fingerprint: 'dev-bypass',
        timestamp: new Date()
      };
    }

    const fingerprint = await this.generateFingerprint();
    const behaviorData = this.calculateBehaviorData();

    const detectionData: BotDetectionData = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      plugins: this.getPluginList(),
      canvasFingerprint: await this.generateCanvasFingerprint(),
      webglFingerprint: await this.generateWebGLFingerprint(),
      audioFingerprint: await this.generateAudioFingerprint(),
      fonts: await this.getFontList(),
      behaviorData,
      recaptchaScore: recaptchaToken ? await this.verifyRecaptcha(recaptchaToken) : 0.7 // Default good score if no token
    };

    const result = this.analyzeBotBehavior(detectionData, formData);
    await this.logBotAttempt(fingerprint, detectionData, result);
    return result;
  }

  private async generateFingerprint(): Promise<string> {
    const components = [
      navigator.userAgent,
      navigator.language,
      navigator.platform,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.maxTouchPoints || 'unknown'
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < components.length; i++) {
      const char = components.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  private getPluginList(): string[] {
    return Array.from(navigator.plugins).map(plugin => plugin.name);
  }

  private async generateCanvasFingerprint(): Promise<string> {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return 'no-canvas';

      // Draw some text and shapes
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Bot Detection Canvas', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Bot Detection Canvas', 4, 17);

      return canvas.toDataURL();
    } catch (e) {
      return 'canvas-error';
    }
  }

  private async generateWebGLFingerprint(): Promise<string> {
    try {
      const canvas = document.createElement('canvas');
      const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
      if (!gl) return 'no-webgl';

      const renderer = gl.getParameter(gl.RENDERER);
      const vendor = gl.getParameter(gl.VENDOR);
      const version = gl.getParameter(gl.VERSION);

      return `${renderer}|${vendor}|${version}`;
    } catch (e) {
      return 'webgl-error';
    }
  }

  private async generateAudioFingerprint(): Promise<string> {
    try {
      // Use a simpler approach to avoid ScriptProcessorNode deprecation
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(10000, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.start(0);
      await new Promise(resolve => setTimeout(resolve, 100));
      oscillator.stop();
      audioContext.close();

      return `audio-${audioContext.sampleRate}-${audioContext.state}`;
    } catch (e) {
      return 'audio-error';
    }
  }

  private async getFontList(): Promise<string[]> {
    const baseFonts = ['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Comic Sans MS', 'Trebuchet MS', 'Arial Black', 'Impact'];
    const testString = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const testSize = '72px';
    const h = document.getElementsByTagName('body')[0];
    const s = document.createElement('span');
    s.style.fontSize = testSize;
    s.innerHTML = testString;
    const defaultWidth: { [key: string]: number } = {};
    const defaultHeight: { [key: string]: number } = {};

    // Get default font measurements
    for (const font of baseFonts) {
      s.style.fontFamily = font;
      h.appendChild(s);
      defaultWidth[font] = s.offsetWidth;
      defaultHeight[font] = s.offsetHeight;
      h.removeChild(s);
    }

    // Detect available fonts
    const availableFonts: string[] = [];
    for (const font of baseFonts) {
      s.style.fontFamily = font;
      h.appendChild(s);
      if (s.offsetWidth !== defaultWidth[font] || s.offsetHeight !== defaultHeight[font]) {
        availableFonts.push(font);
      }
      h.removeChild(s);
    }

    return availableFonts;
  }

  private calculateBehaviorData(): BehaviorData {
    const now = Date.now();
    const formFillTime = now - this.startTime;

    // Calculate typing speed (rough estimate)
    const typingSpeed = this.behaviorTracker.typingSpeed || 0;

    // Calculate hesitation time (average time between activities)
    const hesitationTime = this.clickTimes.length > 1
      ? this.clickTimes.reduce((acc, time, index) => {
        if (index === 0) return 0;
        return acc + (time - this.clickTimes[index - 1]);
      }, 0) / (this.clickTimes.length - 1)
      : 0;

    return {
      ...this.behaviorTracker,
      formFillTime,
      hesitationTime,
      mouseMovements: this.mouseMoveCount,
      errorRate: this.errors
    };
  }

  private analyzeBotBehavior(data: BotDetectionData, formData: any): BotDetectionResult {
    const reasons: string[] = [];
    let confidence = 0;

    // Check reCAPTCHA score (more lenient for development)
    if (data.recaptchaScore !== undefined) {
      if (data.recaptchaScore < 0.05) {
        confidence += 20;
        reasons.push('Extremely low reCAPTCHA score');
      } else if (data.recaptchaScore < 0.2) {
        confidence += 10;
        reasons.push('Low reCAPTCHA score');
      }
      // Only warn about low scores, don't heavily penalize
    }

    // Check behavior patterns
    if (data.behaviorData.typingSpeed > 50) { // Very fast typing
      confidence += 15;
      reasons.push('Unusually fast typing speed');
    }

    if (data.behaviorData.mouseMovements < 5) { // Very few mouse movements
      confidence += 20;
      reasons.push('Minimal mouse movement');
    }

    if (data.behaviorData.clickPatterns.length > 0) {
      const avgClickInterval = data.behaviorData.clickPatterns.reduce((a, b) => a + b, 0) / data.behaviorData.clickPatterns.length;
      if (avgClickInterval < 100) { // Very fast clicking
        confidence += 15;
        reasons.push('Unusually fast clicking pattern');
      }
    }

    if (data.behaviorData.formFillTime < 2000) { // Form filled too quickly
      confidence += 25;
      reasons.push('Form filled too quickly');
    }

    if (data.behaviorData.copyPasteDetected) {
      confidence += 10;
      reasons.push('Copy-paste behavior detected');
    }

    if (data.behaviorData.tabSwitchCount > 5) {
      confidence += 10;
      reasons.push('Excessive tab switching');
    }

    // Only flag perfect completion if it's extremely suspicious (very fast + no errors)
    if (data.behaviorData.errorRate === 0 && data.behaviorData.formFillTime < 3000 && data.behaviorData.typingSpeed > 80) {
      confidence += 10;
      reasons.push('Suspiciously perfect form completion');
    }

    // Check for common bot user agents
    const botUserAgents = [
      'bot', 'crawler', 'spider', 'scraper', 'curl', 'wget', 'python', 'java', 'php'
    ];
    const userAgentLower = data.userAgent.toLowerCase();
    if (botUserAgents.some(bot => userAgentLower.includes(bot))) {
      confidence += 30;
      reasons.push('Bot-like user agent detected');
    }

    // Check for headless browser indicators
    if (!data.plugins.length || data.plugins.length < 3) {
      confidence += 10;
      reasons.push('Limited plugin support (possible headless browser)');
    }

    if (!data.webglFingerprint || data.webglFingerprint === 'no-webgl') {
      confidence += 10;
      reasons.push('No WebGL support (possible headless browser)');
    }

    // Check for suspicious form data patterns
    if (formData.email && formData.email.includes('+')) {
      confidence += 5;
      reasons.push('Suspicious email format');
    }

    if (formData.password && data.behaviorData.typingSpeed > 30) {
      confidence += 10;
      reasons.push('Password typed too quickly');
    }

    // Determine risk level (more lenient thresholds)
    let riskLevel: 'low' | 'medium' | 'high' | 'critical';
    if (confidence >= 80) {
      riskLevel = 'critical';
    } else if (confidence >= 65) {
      riskLevel = 'high';
    } else if (confidence >= 40) {
      riskLevel = 'medium';
    } else {
      riskLevel = 'low';
    }

    return {
      isBot: confidence >= 65, // Raised threshold from 50 to 65
      confidence: Math.min(confidence, 100),
      reasons,
      riskLevel,
      fingerprint: data.behaviorData.toString(), // Simplified for now
      timestamp: new Date()
    };
  }

  private async verifyRecaptcha(token: string): Promise<number> {
    try {
      // In development, if API route doesn't exist, return neutral score
      const isDevelopment = import.meta.env.DEV;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch('/api/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // In dev mode, 404 is expected if API route isn't set up
        if (isDevelopment && response.status === 404) {
          return 0.7; // Return neutral-good score for development
        }
        return 0.5;
      }

      const data = await response.json();

      if (data.success && data.score !== undefined) {
        return data.score;
      } else {
        return 0.5;
      }
    } catch (error: any) {
      // In development, if API doesn't exist, that's okay
      const isDevelopment = import.meta.env.DEV;
      if (isDevelopment && (error.name === 'AbortError' || error.message?.includes('404'))) {
        return 0.7; // Return neutral-good score for development
      }
      return 0.5;
    }
  }

  private async logBotAttempt(fingerprint: string, data: BotDetectionData, result: BotDetectionResult): Promise<void> {
    try {
      // Skip logging in development or if user is not authenticated
      const isDevelopment = import.meta.env.DEV;
      if (isDevelopment) {
        console.log('Bot detection result (not logged in dev):', result);
        return;
      }

      // Build attempt object, only including defined fields
      const attemptData: any = {
        fingerprint,
        userAgent: data.userAgent,
        isBot: result.isBot,
        confidence: result.confidence,
        reasons: result.reasons,
        riskLevel: result.riskLevel,
        timestamp: new Date(),
        behaviorData: data.behaviorData
      };

      // Only add optional fields if they exist
      if (data.ipAddress) {
        attemptData.ipAddress = data.ipAddress;
      }
      if (data.recaptchaScore !== undefined) {
        attemptData.recaptchaScore = data.recaptchaScore;
      }

      await addDoc(collection(db, 'botAttempts'), {
        ...attemptData,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to log bot attempt:', error);
      // Don't throw - logging failures shouldn't block registration
    }
  }

  public async getBotAttempts(limitCount: number = 100): Promise<BotAttempt[]> {
    try {
      const q = query(
        collection(db, 'botAttempts'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as BotAttempt));
    } catch (error) {
      console.error('Failed to get bot attempts:', error);
      return [];
    }
  }

  public async getBotStats(): Promise<{
    totalAttempts: number;
    botAttempts: number;
    humanAttempts: number;
    averageConfidence: number;
    riskLevelDistribution: { [key: string]: number };
  }> {
    try {
      const attempts = await this.getBotAttempts(1000);
      const totalAttempts = attempts.length;
      const botAttempts = attempts.filter(a => a.isBot).length;
      const humanAttempts = totalAttempts - botAttempts;
      const averageConfidence = attempts.reduce((sum, a) => sum + a.confidence, 0) / totalAttempts;

      const riskLevelDistribution = attempts.reduce((acc, a) => {
        acc[a.riskLevel] = (acc[a.riskLevel] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number });

      return {
        totalAttempts,
        botAttempts,
        humanAttempts,
        averageConfidence: Math.round(averageConfidence * 100) / 100,
        riskLevelDistribution
      };
    } catch (error) {
      console.error('Failed to get bot stats:', error);
      return {
        totalAttempts: 0,
        botAttempts: 0,
        humanAttempts: 0,
        averageConfidence: 0,
        riskLevelDistribution: {}
      };
    }
  }

  public resetBehaviorTracking(): void {
    this.behaviorTracker = {
      typingSpeed: 0,
      mouseMovements: 0,
      clickPatterns: [],
      scrollBehavior: 0,
      formFillTime: 0,
      hesitationTime: 0,
      errorRate: 0,
      copyPasteDetected: false,
      tabSwitchCount: 0,
      focusTime: 0
    };
    this.startTime = Date.now();
    this.lastActivity = Date.now();
    this.mouseMoveCount = 0;
    this.clickTimes = [];
    this.errors = 0;
  }

  public trackTypingSpeed(characters: number, timeMs: number): void {
    this.behaviorTracker.typingSpeed = (characters / timeMs) * 1000; // chars per second
  }

  public trackError(): void {
    this.errors++;
  }

  public trackCopyPaste(): void {
    this.behaviorTracker.copyPasteDetected = true;
  }
}

export default BotDetectionService;
